/**
 * src/utils/siteCache.ts
 *
 * Index sites/clients par id (Map), dérivé du cache déjà tenu par settingsApi.ts —
 * pas un second cache : aucun état "loaded"/invalidation propre ici. La donnée
 * source (arrays) est mise en cache et invalidée une seule fois, dans
 * settingsApi.ts (`invalidateClients()`/`invalidateSites()`, appelées
 * automatiquement par `upsertClient`/`deleteClient`/`upsertSite`/`deleteSite`).
 * Ce module se contente de reconstruire les deux Map d'index à partir de cette
 * donnée — reconstruction bon marché (quelques centaines d'entrées, une fois par
 * montage à froid), cf. docs/authentification.md non concerné ici, juste un choix
 * de simplicité au lieu d'un double suivi d'état.
 *
 * Client ID is the segment before '@' in the site ID
 * (e.g. "villeDeLyon@69002" → clientId "villeDeLyon").
 */

import { useState, useEffect } from "react";
import {
  listSites, listClients, getCachedSites, getCachedClients,
  type SiteRecord, type ClientRecord,
} from "../api/settingsApi";

interface Index {
  sites:   Map<string, SiteRecord>;
  clients: Map<string, ClientRecord>;
}

function buildIndex(sites: SiteRecord[], clients: ClientRecord[]): Index {
  return {
    sites:   new Map(sites.map(s => [s.id, s])),
    clients: new Map(clients.map(c => [c.id, c])),
  };
}

function cachedIndex(): Index | null {
  const sites   = getCachedSites();
  const clients = getCachedClients();
  return sites && clients ? buildIndex(sites, clients) : null;
}

export function useSiteCache() {
  const [index, setIndex] = useState<Index | null>(cachedIndex);

  useEffect(() => {
    if (index) return;
    let active = true;
    Promise.all([listSites(), listClients()]).then(([sites, clients]) => {
      if (active) setIndex(buildIndex(sites, clients));
    });
    return () => { active = false; };
  }, [index]);

  return {
    ready: index !== null,
    getSiteName(siteId: string): string {
      const site = index?.sites.get(siteId);
      if (site) return site.name;
      // Rapport multi-sites : siteId synthétique "{clientId}@multi-{id1,id2,…}"
      // (cf. ReportCreator::buildMultiInstance) → noms des sites séparés par une virgule.
      const multiIds = siteId.split("@multi-")[1]?.split(",").filter(Boolean);
      if (index && multiIds?.length) {
        const names = multiIds.map(id => index.sites.get(id)?.name).filter((n): n is string => !!n);
        if (names.length) return names.join(", ");
      }
      return siteId;
    },
    getClientName(siteId: string): string {
      const clientId = siteId.split("@")[0];
      return index?.clients.get(clientId)?.name ?? clientId;
    },
  };
}
